import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@material-ui/core';
import { alpha, useTheme } from '@material-ui/core/styles';
import { Icon } from '@iconify/react';
import undoOutline from '@iconify/icons-eva/undo-outline';
import { getRevertOptionDescription, getRevertOptionKey } from 'src/utils/revertStatusOptions';

const STATUS_COLORS = {
  'Awaiting Approval': 'warning',
  Approved: 'success',
  'Pre-Production': 'primary',
  Production: 'secondary',
};

export default function RevertStatusDialog({
  open,
  onClose,
  options = [],
  selectedKey,
  onSelect,
  onConfirm,
  isSubmitting = false,
}) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 400,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
          color: theme.palette.common.white,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.common.white, 0.18),
            }}
          >
            <Icon icon={undoOutline} width={20} height={20} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Revert Status
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.92 }}>
              Choose where to move this order
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Stack spacing={1}>
          {options.map((opt) => {
            const optKey = getRevertOptionKey(opt);
            const isSelected = selectedKey === optKey;
            const colorKey = STATUS_COLORS[opt.label] || 'warning';

            return (
              <Box
                key={optKey}
                onClick={() => onSelect(optKey)}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.25,
                  borderRadius: 1.5,
                  border: '1.5px solid',
                  borderColor: isSelected
                    ? theme.palette[colorKey].main
                    : theme.palette.divider,
                  bgcolor: isSelected
                    ? alpha(theme.palette[colorKey].main, 0.1)
                    : alpha(theme.palette.background.default, 0.6),
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette[colorKey].main,
                    bgcolor: alpha(theme.palette[colorKey].main, 0.08),
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => onSelect(optKey)}
                  color={colorKey}
                  size="small"
                  sx={{ p: 0.25, mt: 0.15 }}
                  onClick={(e) => e.stopPropagation()}
                />
                <Box sx={{ pt: 0.15, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.3 }}
                  >
                    {opt.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}
                  >
                    {getRevertOptionDescription(opt)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          pt: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <Button onClick={onClose} color="inherit" size="small" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={onConfirm}
          disabled={!selectedKey || isSubmitting}
          sx={{ minWidth: 120, borderRadius: 1.5, fontWeight: 600 }}
        >
          {isSubmitting ? 'Reverting...' : 'Confirm Revert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
